import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { getDeadSpans, getLiveSpans, type ShelfDeadSpan } from "./shelfMerchandising";

/**
 * A RACK is a set of partial boards (tiers) standing on a host shelf, e.g. the
 * wipes rack on a toilet paper wall. A board is a rack's when its assembly ends
 * with "RACK" (trimmed, any case); the assembly, upper-cased and single-spaced,
 * is the rack's name (as owa-backend's rackBoards.ts reads it).
 */
export const rackNameOf = (assembly: string | null | undefined): string | null => {
  if (typeof assembly !== "string") return null;
  const name = assembly.trim().toUpperCase().replace(/\s+/g, " ");
  return name.endsWith("RACK") ? name : null;
};

export interface BayRack {
  /** Unique in its bay: `${name}@${host.uniqueId}` with a stored host, else the name. */
  key: string;
  name: string;
  /** The rack's boards, top first. */
  boards: PlanogramShelf[];
  /**
   * Its boards below the host's top surface or level with an ordinary board under their
   * live part: a data error, kept in the rack so it can be shown.
   */
  collidingBoards: PlanogramShelf[];
  /** The board the rack stands on: its boards' `standsOn`, else guessed. Null with none. */
  host: PlanogramShelf | null;
  /** The rack's live edges in pog x: where its boards' live parts begin and end. */
  xLeft: number;
  xRight: number;
  /** Where the rack stands: the top of its host board, or the bay floor without one. */
  yBase: number;
  /** The top of its top board. */
  yTop: number;
  /** The lowest board's bottom, and the host's (null without one). */
  yLowestBoard: number;
  yHost: number | null;
}

/**
 * The racks of a bay: boards of one rack name on one host, the ordinary board their `standsOn`
 * names. Boards without one group by name, their host the nearest ordinary board strictly below.
 */
export const getBayRacks = (bay: PlanogramBay): BayRack[] => {
  const groups = new Map<
    string,
    { name: string; host: PlanogramShelf | null; boards: PlanogramShelf[] }
  >();
  for (const shelf of bay.shelves) {
    const name = rackNameOf(shelf.assembly);
    if (name === null) continue;
    const host =
      bay.shelves.find(
        (other) =>
          shelf.standsOn !== null &&
          other.uniqueId === shelf.standsOn &&
          rackNameOf(other.assembly) === null,
      ) ?? null;
    const key = host ? `${name}@${host.uniqueId}` : name;
    const group = groups.get(key) ?? { name, host, boards: [] };
    group.boards.push(shelf);
    groups.set(key, group);
  }

  return [...groups].flatMap(([key, group]) => {
    const boards = [...group.boards].sort((left, right) => right.getYBottom() - left.getYBottom());
    const spans = boards.flatMap((board) => board.getLiveXSpans());
    if (spans.length === 0) return [];
    const top = boards[0];
    const lowest = boards[boards.length - 1];
    const yLowestBoard = lowest.getYBottom();
    const host =
      group.host ??
      bay.shelves
        .filter((shelf) => rackNameOf(shelf.assembly) === null && shelf.getYBottom() < yLowestBoard)
        .sort((left, right) => right.getYBottom() - left.getYBottom())[0] ??
      null;
    const yBase = host ? host.getYBottom() + host.thickness : bay.baseHeight;
    const ordinary = bay.shelves.filter((shelf) => rackNameOf(shelf.assembly) === null);
    const collidingBoards = boards.filter((board) => {
      const yBottom = board.getYBottom();
      const yTop = yBottom + board.thickness;
      return (
        yBottom < yBase ||
        ordinary.some(
          (other) =>
            other.getYBottom() < yTop &&
            other.getYBottom() + other.thickness > yBottom &&
            board
              .getLiveXSpans()
              .some((span) => other.getXLeft() < span.xRight && other.getXRight() > span.xLeft),
        )
      );
    });

    return [
      {
        key,
        name: group.name,
        boards,
        collidingBoards,
        host,
        xLeft: Math.min(...spans.map((span) => span.xLeft)),
        xRight: Math.max(...spans.map((span) => span.xRight)),
        yBase,
        yTop: top.getYBottom() + top.thickness,
        yLowestBoard,
        yHost: host ? host.getYBottom() : null,
      },
    ];
  });
};

/** Whether a rack board collides with a board of its bay (see `BayRack.collidingBoards`). */
export const isCollidingRackBoard = (shelf: PlanogramShelf): boolean =>
  rackNameOf(shelf.assembly) !== null &&
  getBayRacks(shelf.bay).some((rack) => rack.collidingBoards.includes(shelf));

/** `spans` with `cover` cut out of them, both shelf-relative. */
const subtractSpans = (
  spans: readonly ShelfDeadSpan[],
  cover: readonly ShelfDeadSpan[],
): ShelfDeadSpan[] =>
  cover.reduce<ShelfDeadSpan[]>(
    (left, hole) =>
      left.flatMap((span) => {
        const holeEnd = hole.x + hole.width;
        const spanEnd = span.x + span.width;
        if (hole.x >= spanEnd || holeEnd <= span.x) return [span];
        return [
          ...(hole.x > span.x ? [{ x: span.x, width: hole.x - span.x }] : []),
          ...(holeEnd < spanEnd ? [{ x: holeEnd, width: spanEnd - holeEnd }] : []),
        ];
      }),
    [...spans],
  );

/**
 * How a board is drawn. A rack's host runs under the rack at full width: the
 * rack stands on it, so the dead span under the rack is not hatched. A rack's
 * own board is drawn over its live part only. Any other board is not drawn
 * where another board fills one of its dead spans (a board beside it at the
 * same notch is live there, or a rack of its bay spans its height there).
 * Every other dead span (a pole, a legacy right-end strip) stays part of the
 * board, flagged.
 *
 * `x`/`width` (shelf-relative) run from the first drawn x to the last; the
 * whole board when nothing of it is drawn. `flaggedDeadSpans` are the spans
 * to hatch.
 */
export const getBoardDrawing = (
  shelf: PlanogramShelf,
): { x: number; width: number; flaggedDeadSpans: ShelfDeadSpan[] } => {
  const dead = getDeadSpans(shelf);
  const whole = { x: 0, width: shelf.width, flaggedDeadSpans: dead };
  if (dead.length === 0) return whole;

  let flagged: ShelfDeadSpan[] = [];
  if (rackNameOf(shelf.assembly) === null) {
    const xLeft = shelf.getXLeft();
    const racks = getBayRacks(shelf.bay);
    const footprintOf = (rack: BayRack): ShelfDeadSpan => ({
      x: rack.xLeft - xLeft,
      width: rack.xRight - rack.xLeft,
    });
    const hosted = racks.filter((rack) => rack.host === shelf);
    if (hosted.length > 0) {
      return { ...whole, flaggedDeadSpans: subtractSpans(dead, hosted.map(footprintOf)) };
    }

    const yBottom = shelf.getYBottom();
    const cover: ShelfDeadSpan[] = [];
    for (const other of shelf.bay.shelves) {
      if (other === shelf || other.getYBottom() !== yBottom) continue;
      cover.push(...getLiveSpans(other));
    }
    for (const rack of racks) {
      const from = rack.yHost ?? rack.yLowestBoard;
      if (yBottom < from || yBottom > rack.boards[0].getYBottom()) continue;
      cover.push(footprintOf(rack));
    }
    flagged = subtractSpans(dead, cover);
  }

  const filled = subtractSpans(dead, flagged);
  const drawn = subtractSpans([{ x: 0, width: shelf.width }], filled);
  const first = drawn[0];
  const last = drawn[drawn.length - 1];
  if (!first || !last) return { ...whole, flaggedDeadSpans: flagged };
  return { x: first.x, width: last.x + last.width - first.x, flaggedDeadSpans: flagged };
};
